import { useLocalization } from "@fluent/react";
import { Dialog, phoneFormatter, useClipboard } from "cerulean";
import { entities, parseUrlLabel } from "byzantine";
import { Alert, Tooltip } from "@narmi/design_system";
import { useInstitutionSettings } from "../../../contexts/InstitutionSettingsContext";
import ContactInfo from "../ContactInfo";

import styles from "./SubscriptionCancelInfoDialog.module.scss";

export interface SubscriptionCancelInfoDialogProps {
  merchantName?: string;
  isOpen: boolean;
  onUserDismiss: () => void;
}

const SubscriptionCancelInfoDialog = ({
  merchantName,
  isOpen,
  onUserDismiss,
}: SubscriptionCancelInfoDialogProps) => {
  const { long_name: fiName } = useInstitutionSettings();
  const { l10n } = useLocalization();
  const merchant = entities.merchants.useMerchant(merchantName || "");
  const { copy: copyPhoneNumber, status: phoneNumberCopyStatus } = useClipboard(
    `${merchant?.contact_number}`
  );
  const { copy: copyEmail, status: emailCopyStatus } = useClipboard(
    `${merchant?.contact_email}`
  );
  const dialogTitle = l10n.getString(
    "merchant-details-dialog-title-cancel",
    null,
    "Need to cancel your subscription?"
  );
  const dialogContent = l10n.getString(
    "merchant-details-dialog-content-convenience",
    { fiName },
    `By clicking these links you will be redirected to the merchant's site.\nThis information is provided as a convenience. ${fiName} has no partnership with this merchant.`
  );
  const contactPhone = l10n.getString(
    "merchant-details-contactinfo-name-phone",
    null,
    "Phone"
  );
  const contactEmail = l10n.getString(
    "merchant-details-contactinfo-name-email",
    null,
    "Email"
  );
  const contactWebsite = l10n.getString(
    "merchant-details-contactinfo-name-website",
    null,
    "Website"
  );

  const noContactInfoMessage = l10n.getString(
    "merchant-details-dialog-no-contact-info",
    null,
    "Please contact this merchant directly to cancel this subscription."
  );

  const prettyUrl = merchant?.cancellation_url
    ? parseUrlLabel(merchant?.cancellation_url)
    : null;

  const hasInfo = Boolean(
    merchant?.contact_number || merchant?.contact_email || prettyUrl
  );

  if (!hasInfo) {
    return (
      <Dialog isOpen={isOpen} onUserDismiss={onUserDismiss} title={dialogTitle}>
        <div className={styles.noContactInfoContainer}>
          <Alert isActive isDismissable={false} kind="warn">
            {noContactInfoMessage}
          </Alert>
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog isOpen={isOpen} onUserDismiss={onUserDismiss} title={dialogTitle}>
      <div className={styles.container}>
        {merchant?.contact_number && (
          <ContactInfo
            name={contactPhone}
            value={
              <div
                className={styles.copyable}
                onClick={copyPhoneNumber}
                role="button"
                tabIndex={0}
              >
                <Tooltip text={phoneNumberCopyStatus}>
                  {phoneFormatter(merchant.contact_number)}
                </Tooltip>
              </div>
            }
          />
        )}
        {merchant?.contact_email && (
          <ContactInfo
            name={contactEmail}
            value={
              <div
                className={styles.copyable}
                onClick={copyEmail}
                role="button"
                tabIndex={0}
              >
                <Tooltip text={emailCopyStatus}>
                  {merchant.contact_email}
                </Tooltip>
              </div>
            }
          />
        )}
        {merchant?.cancellation_url && (
          <ContactInfo
            name={contactWebsite}
            value={
              <a
                href={merchant.cancellation_url}
                target="_blank"
                rel="noopener noreferrer"
              >
                {prettyUrl}
              </a>
            }
          />
        )}
        <div className={styles.dialogContent}>{dialogContent}</div>
      </div>
    </Dialog>
  );
};

export default SubscriptionCancelInfoDialog;
